<template>
  <div class="details-wrapper bg-light min-vh-100">
    <!-- Header -->
    <div class="bg-dark text-white d-flex align-items-center px-3 py-2">
      <button
        class="btn btn-link text-white p-0 me-3 mr-3 py-2"
        @click="$router.back()"
      >
        <i class="fas fa-arrow-left"></i>
      </button>

      <strong>{{ $t("account_page_timeline.details_header") }}</strong>
    </div>

    <div v-if="isLoaded && booking" class="p-3">
      <!-- Guest -->
      <div class="card shadow-sm mb-3">
        <div class="card-header bg-info text-white fw-bold">
          {{ booking.full_name }}
        </div>

        <div class="card-body">
          <div class="row border-bottom py-2">
            <div class="col-5 fw-bold">
              {{ $t("account_page_timeline.room_no") }}
            </div>

            <div class="col-7">
              {{ booking.room_number || "--" }}
            </div>
          </div>

          <div class="row border-bottom py-2">
            <div class="col-5 fw-bold">
              {{ $t("account_page_timeline.no_of_rooms") }}
            </div>

            <div class="col-7">
              {{ booking.number_of_rooms || "--" }}
            </div>
          </div>

          <div class="row border-bottom py-2">
            <div class="col-5 fw-bold">
              {{ $t("account_page_timeline.rsv_no") }}
            </div>

            <div class="col-7">
              {{ booking.pms_reservation_no ?? "--" }}
            </div>
          </div>

          <div class="row border-bottom py-2">
            <div class="col-5 fw-bold">
              {{ $t("account_page_timeline.no_of_guests") }}
            </div>

            <div class="col-7">
              {{ booking.number_of_guests ?? "--" }}
            </div>
          </div>

          <div class="row border-bottom py-2">
            <div class="col-5 fw-bold">
              {{ $t("account_page_timeline.ci_co") }}
            </div>

            <div class="col-7">
              {{ getBookingDateRange(booking) }}
            </div>
          </div>

          <div class="row border-bottom py-2">
            <div class="col-5 fw-bold">
              {{ $t("account_page_timeline.total") }}
            </div>

            <div class="col-7">
              <span class="badge" :class="paymentStatusClass(booking)">
                {{ booking.total_charge ?? "--" }}
              </span>
            </div>
          </div>

          <div class="row border-bottom py-2">
            <div class="col-5 fw-bold">
              {{ $t("account_page_timeline.payment") }}
            </div>
            <div class="col-7">
              {{ $t(`payment_statuses.${booking.payment_status_code}`) }}
            </div>
          </div>

          <div class="row border-bottom py-2">
            <div class="col-5 fw-bold">
              {{ $t("account_page_timeline.key_code") }}
            </div>

            <div class="col-7">
              <span v-if="!checkoutCodes.includes(booking.booking_status_code)">
                {{ booking.key_code ?? "--" }}
              </span>
            </div>
          </div>

          <div class="row border-bottom py-2">
            <div class="col-5 fw-bold">
              {{ $t("account_page_timeline.pre_check_in") }}<br />
              {{ $t("account_page_timeline.checkin") }}<br />
              {{ $t("account_page_timeline.checkout") }}
            </div>

            <div class="col-7">
              {{ formatDateTime(booking.pre_checkin_at) }}<br />
              {{ formatDateTime(booking.checked_in_at) }}<br />
              {{ formatDateTime(booking.checked_out_at) }}
            </div>
          </div>

          <!-- Passport -->
          <div class="row align-items-center py-2">
            <div class="col-5 fw-bold">
              {{ $t("account_page_timeline.id_passport") }}
            </div>

            <div class="col-7">
              <button
                v-if="booking.gov_id"
                class="btn btn-secondary btn-sm w-100"
                @click="$set(booking, 'showGovId', !booking.showGovId)"
              >
                {{
                  booking.showGovId
                    ? $t("account_page_timeline.hide")
                    : $t("account_page_timeline.show")
                }}
              </button>

              <button v-else class="btn btn-secondary btn-sm w-100" disabled>
                {{ $t("account_page_timeline.no_id") }}
              </button>
            </div>

            <div
              v-if="booking.gov_id && booking.showGovId"
              class="col-12 mt-2 text-center border"
            >
              <img
                :src="booking.gov_id"
                class="img-fluid border rounded my-2"
                style="max-height: 300px"
                alt="Government ID"
              />
            </div>
          </div>
          <!-- End of passport -->
        </div>
      </div>
    </div>

    <div v-else class="text-center py-5">Loading...</div>
  </div>
</template>
<script>
import moment from "moment";
export default {
  name: "GuestTimelineDetails",

  data() {
    return {
      slug_name: this.$route.params.slug_name,
      pass: this.$route.params.pass,
      reservation_no: this.$route.params.reservation_no,
      booking: null,
      isLoaded: false,
      checkoutCodes: ["102", "103", "109", "110", "111", "116"],
      checkinCodes: ["201", "208"]
    };
  },

  async mounted() {
    await this.fetchBooking();
  },

  methods: {
    async fetchBooking() {
      this.isLoaded = false;

      try {
        const response = await this.$lionheart.get(
          `/v2/guest-timeline/${this.slug_name}/booking`,
          {
            params: {
              pass: this.pass || "",
              reservation_no: this.reservation_no || "",
            },
          }
        );

        this.booking = response.data.data.booking_details;
      } catch (error) {
        console.error("Error fetching booking:", error);
      } finally {
        this.isLoaded = true;
      }
    },
    paymentStatusClass(item) {
      return this.checkinCodes.includes(String(item.payment_status_code))
        ? "bg-success"
        : "bg-warning text-dark";
    },
    formatDateTime(datetime, mode = "datetime") {
      if (!datetime) return "--";

      const formatString = mode === "date" ? "YYYY/MM/DD" : "YYYY/MM/DD HH:mm";
      return moment(datetime).format(formatString);
    },
    getBookingDateRange(booking) {
      if (!booking) return "-- - --";

      // Determine which dates to use based on pms_reservation_no
      const checkin = booking.pms_reservation_no
        ? booking.checkin_date
        : booking.checked_in_at;
      const checkout = booking.pms_reservation_no
        ? booking.checkout_date
        : booking.checked_out_at;

      return `${this.formatDateTime(checkin, "date")} - ${this.formatDateTime(
        checkout,
        "date"
      )}`;
    },
  },
};
</script>

<style scoped>
.details-wrapper {
  min-height: 100vh;
}

.card {
  border: none;
  border-radius: 12px;
}

.card-header {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.btn-link {
  text-decoration: none;
}

.btn-link:focus {
  box-shadow: none;
}

.row {
  align-items: center;
}
</style>